<template>
  <div class="p-4 md:p-6 lg:p-8 bg-app-bg-light dark:bg-app-bg-dark min-h-screen">
    <!-- Header -->
    <div class="flex items-center gap-3 mb-6">
      <button
        @click="$router.push('/inicio')"
        class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-custom-primary dark:hover:text-custom-primary transition-colors border-0 bg-transparent"
      >
        <i class="bx bx-arrow-back text-lg"></i>
        Regresar
      </button>
      <div class="h-5 w-px bg-slate-300 dark:bg-slate-600"></div>
      <div>
        <h1 class="text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100">Caja Chica de Administración</h1>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          Se alimenta automáticamente con los cobros en efectivo no facturado. Independiente de la Caja Chica Hotel.
        </p>
      </div>
    </div>

    <!-- Saldo actual -->
    <div class="bg-custom-primary/10 dark:bg-custom-primary/20 rounded-xl p-5 md:p-6 shadow-sm mb-6 flex items-center justify-between">
      <div>
        <p class="text-xs font-semibold text-custom-primary uppercase tracking-wider mb-1">Saldo Actual</p>
        <p class="text-3xl font-bold text-custom-primary">Q{{ fmt(saldoActual) }}</p>
      </div>
      <div class="flex gap-4 text-right">
        <div>
          <p class="text-xs text-green-600 dark:text-green-400">Total Debe</p>
          <p class="text-base font-bold text-green-700 dark:text-green-300">Q{{ fmt(totales.debe) }}</p>
        </div>
        <div>
          <p class="text-xs text-red-600 dark:text-red-400">Total Haber</p>
          <p class="text-base font-bold text-red-700 dark:text-red-300">Q{{ fmt(totales.haber) }}</p>
        </div>
      </div>
    </div>

    <!-- Formulario registrar gasto -->
    <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm mb-6">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-sm font-bold text-slate-800 dark:text-slate-100">Registrar Gasto</h2>
        <button v-if="!mostrarFormularioGasto" @click="mostrarFormularioGasto = true" class="btn btn-success btn-sm">
          <i class="bx bx-plus mr-1"></i> Nuevo Gasto
        </button>
      </div>
      <form v-if="mostrarFormularioGasto" @submit.prevent="registrarGasto" class="flex flex-wrap gap-3 items-end">
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Monto *</label>
          <input v-model.number="formGasto.monto" type="number" step="0.01" min="0.01" class="form-control form-control-sm" required />
        </div>
        <div class="flex-1 min-w-[220px]">
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Concepto (para qué fue realizado) *</label>
          <input v-model="formGasto.concepto" type="text" class="form-control form-control-sm" required maxlength="500" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Núm. Documento</label>
          <input v-model="formGasto.numero_documento" type="text" class="form-control form-control-sm" maxlength="200" />
        </div>
        <div class="flex-1 min-w-[220px]">
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Observaciones</label>
          <input v-model="formGasto.observaciones" type="text" class="form-control form-control-sm" maxlength="2500" />
        </div>
        <button type="button" class="btn btn-secondary btn-sm" @click="cancelarFormularioGasto">Cancelar</button>
        <button type="submit" class="btn btn-primary btn-sm" :disabled="guardandoGasto">
          <span v-if="guardandoGasto" class="spinner-border spinner-border-sm me-1"></span>
          Guardar Gasto
        </button>
      </form>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm mb-6">
      <div class="flex flex-wrap gap-3 items-end">
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Desde</label>
          <input type="date" v-model="filtro.fecha_inicio" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Hasta</label>
          <input type="date" v-model="filtro.fecha_fin" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Tipo</label>
          <select v-model="filtro.tipo" class="form-control form-control-sm">
            <option value="">Todos</option>
            <option value="debe">Debe</option>
            <option value="haber">Haber</option>
          </select>
        </div>
        <button @click="cargar" class="btn btn-outline-secondary btn-sm">
          <i class="bx bx-search mr-1"></i> Filtrar
        </button>
        <button @click="generarPDF" class="btn btn-outline-primary btn-sm" :disabled="!filtro.fecha_inicio || !filtro.fecha_fin">
          <i class="bx bx-file-pdf mr-1"></i> Generar PDF
        </button>
      </div>
    </div>

    <!-- Tabla -->
    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-xs md:text-sm">
          <thead>
            <tr class="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
              <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">Fecha</th>
              <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Concepto</th>
              <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Documento</th>
              <th class="px-3 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Debe</th>
              <th class="px-3 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Haber</th>
              <th class="px-3 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Saldo</th>
              <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Empleado</th>
              <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Estado</th>
              <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Acción</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!movimientos.length">
              <td colspan="9" class="text-center py-6 text-slate-400">No hay movimientos para el período seleccionado.</td>
            </tr>
            <tr
              v-for="m in movimientos"
              :key="m.id"
              class="border-b border-slate-100 dark:border-slate-700"
              :class="{ 'opacity-50': m.anulado }"
            >
              <td class="px-3 py-2 whitespace-nowrap">{{ formatFecha(m.fecha) }}</td>
              <td class="px-3 py-2" :class="{ 'line-through': m.anulado }">{{ m.concepto }}</td>
              <td class="px-3 py-2">{{ m.numero_documento || '—' }}</td>
              <td class="px-3 py-2 text-right font-mono text-green-700 dark:text-green-400">{{ m.tipo === 'debe' ? fmt(m.monto) : '' }}</td>
              <td class="px-3 py-2 text-right font-mono text-red-700 dark:text-red-400">{{ m.tipo === 'haber' ? fmt(m.monto) : '' }}</td>
              <td class="px-3 py-2 text-right font-mono">{{ fmt(m.saldo_resultante) }}</td>
              <td class="px-3 py-2">{{ empleadoNombre(m.empleado) }}</td>
              <td class="px-3 py-2">
                <span v-if="m.anulado" class="badge bg-secondary">Anulado</span>
                <span v-else class="badge" :class="m.tipo === 'debe' ? 'bg-success' : 'bg-warning text-dark'">Vigente</span>
              </td>
              <td class="px-3 py-2">
                <button
                  v-if="esAdmin && !m.anulado"
                  class="btn btn-sm btn-outline-danger"
                  @click="abrirConfirmarAnular(m)"
                >
                  <i class="bx bx-x-circle mr-1"></i> Anular
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal confirmar anulación -->
    <div v-if="movimientoParaAnular" class="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center" style="z-index: 1056">
      <div class="bg-white p-4 rounded shadow" style="min-width: 320px">
        <h5 class="mb-3">Confirmar Anulación</h5>
        <p>
          ¿Anular el movimiento <strong>{{ movimientoParaAnular.concepto }}</strong>
          por Q{{ fmt(movimientoParaAnular.monto) }}?
        </p>
        <p class="text-danger small">Esta acción ajusta el saldo de la caja chica de administración.</p>
        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-secondary" @click="movimientoParaAnular = null">Cancelar</button>
          <button class="btn btn-danger" @click="confirmarAnular" :disabled="anulando">
            <span v-if="anulando" class="spinner-border spinner-border-sm me-1"></span>
            Sí, anular
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_URL } from '../../config/config.js';
import store from '@/store';

export default {
  name: 'CajaChicaAdmin',
  data() {
    const hoy = new Date().toISOString().substring(0, 10);
    return {
      movimientos: [],
      totales: { debe: 0, haber: 0 },
      saldoActual: 0,
      filtro: { fecha_inicio: hoy, fecha_fin: hoy, tipo: '' },
      mostrarFormularioGasto: false,
      guardandoGasto: false,
      formGasto: { monto: null, concepto: '', numero_documento: '', observaciones: '' },
      movimientoParaAnular: null,
      anulando: false,
    };
  },
  computed: {
    esAdmin() {
      return store.getters.getUser?.IdRol === 1;
    },
  },
  methods: {
    fmt(num) {
      return new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num ?? 0);
    },
    formatFecha(f) {
      if (!f) return '—';
      return new Date(f.replace(' ', 'T')).toLocaleString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    empleadoNombre(empleado) {
      if (!empleado) return 'N/A';
      return `${empleado.Nombres ?? ''} ${empleado.Apellidos ?? ''}`.trim() || 'N/A';
    },
    buildParams() {
      const params = {};
      if (this.filtro.fecha_inicio) params.fecha_inicio = this.filtro.fecha_inicio;
      if (this.filtro.fecha_fin) params.fecha_fin = this.filtro.fecha_fin;
      if (this.filtro.tipo) params.tipo = this.filtro.tipo;
      return params;
    },
    async cargar() {
      try {
        const { data } = await axios.get(`${API_URL}/caja-chica-admin`, { params: this.buildParams() });
        this.movimientos = data.data;
        this.totales = data.totales;
        this.saldoActual = data.saldo_actual;
      } catch (e) {
        console.error(e);
        alert(e.response?.data?.message || 'Error al cargar la caja chica de administración.');
      }
    },
    cancelarFormularioGasto() {
      this.mostrarFormularioGasto = false;
      this.formGasto = { monto: null, concepto: '', numero_documento: '', observaciones: '' };
    },
    async registrarGasto() {
      this.guardandoGasto = true;
      try {
        await axios.post(`${API_URL}/caja-chica-admin/gasto`, this.formGasto);
        this.cancelarFormularioGasto();
        await this.cargar();
      } catch (e) {
        alert(e.response?.data?.message || 'Error al registrar el gasto.');
      } finally {
        this.guardandoGasto = false;
      }
    },
    abrirConfirmarAnular(m) {
      this.movimientoParaAnular = m;
    },
    async confirmarAnular() {
      if (!this.movimientoParaAnular) return;
      this.anulando = true;
      try {
        await axios.post(`${API_URL}/caja-chica-admin/${this.movimientoParaAnular.id}/anular`);
        this.movimientoParaAnular = null;
        await this.cargar();
      } catch (e) {
        alert(e.response?.data?.message || 'Error al anular el movimiento.');
      } finally {
        this.anulando = false;
      }
    },
    async generarPDF() {
      try {
        const response = await axios.get(`${API_URL}/caja-chica-admin/pdf`, {
          params: this.buildParams(),
          responseType: 'blob',
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `caja-chica-admin_${this.filtro.fecha_inicio}_${this.filtro.fecha_fin}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } catch (e) {
        if (e.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          try {
            const json = JSON.parse(text);
            alert(json.message || 'Error al generar el PDF.');
          } catch { alert('Error al generar el PDF.'); }
        } else {
          console.error(e);
          alert('Error al generar el PDF.');
        }
      }
    },
  },
  mounted() {
    this.cargar();
  },
};
</script>
